import React, { useEffect, useMemo, useState } from 'react';
import { formatUnits, parseUnits } from 'ethers';
import { useWallet } from '../context/WalletContext';
import { useContracts } from '../hooks/useContracts';
import { useNotify } from '../context/NotificationContext';
import { fmtGRX, fmtUSD } from '../utils/format';
import { parseError } from '../utils/errors';
import GlassCard from '../components/common/GlassCard';
import NeonButton from '../components/common/NeonButton';
import ConnectPrompt from '../components/common/ConnectPrompt';
import HexLoader from '../components/loader/HexLoader';
import Icon from '../components/common/Icon';

const DEADLINE_MINUTES = 20;
const DEFAULT_SLIPPAGE = '1.0';

const Swap = () => {
  const { account } = useWallet();
  const c = useContracts();
  const notify = useNotify();

  const [dir, setDir] = useState('grxToUsdt');
  const [amountIn, setAmountIn] = useState('');
  const [amountOut, setAmountOut] = useState('');
  const [priceStr, setPriceStr] = useState('');
  const [slippage, setSlippage] = useState(DEFAULT_SLIPPAGE);
  const [busy, setBusy] = useState(false);
  const [txLabel, setTxLabel] = useState('');

  const [grxBal, setGrxBal] = useState(0n);
  const [usdtBal, setUsdtBal] = useState(0n);
  const [tickerPrice, setTickerPrice] = useState(null);

  useEffect(() => {
    let mounted = true;
    if (!account) return;
    const load = async () => {
      try {
        const [g, u] = await Promise.all([
          c.read.grx.balanceOf(account).catch(() => 0n),
          c.read.usdt.balanceOf(account).catch(() => 0n),
        ]);
        if (mounted) { setGrxBal(BigInt(g)); setUsdtBal(BigInt(u)); }
      } catch {}
    };
    load();
    const t = setInterval(load, 15000);
    return () => { mounted = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!c.hasRouter) return;
      try {
        const one = parseUnits('1', 18);
        const amounts = await c.read.router.getAmountsOut(one, [c.addresses.grx, c.addresses.usdt]);
        if (mounted) setTickerPrice(BigInt(amounts[1]));
      } catch {
        if (mounted) setTickerPrice(null);
      }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { mounted = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.hasRouter]);

  const fromToken = dir === 'grxToUsdt'
    ? { addr: c.addresses.grx,  symbol: 'GRX',  balance: grxBal,  decimals: 18, logo: '/grx.png' }
    : { addr: c.addresses.usdt, symbol: 'USDT', balance: usdtBal, decimals: 18, logo: '/usdt.png' };

  const toToken = dir === 'grxToUsdt'
    ? { addr: c.addresses.usdt, symbol: 'USDT', balance: usdtBal, decimals: 18, logo: '/usdt.png' }
    : { addr: c.addresses.grx,  symbol: 'GRX',  balance: grxBal,  decimals: 18, logo: '/grx.png' };

  const path = useMemo(() => [fromToken.addr, toToken.addr], [fromToken.addr, toToken.addr]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setAmountOut('');
      setPriceStr('');
      if (!c.hasRouter || !amountIn) return;
      let inWei;
      try { inWei = parseUnits(amountIn, fromToken.decimals); } catch { return; }
      if (inWei <= 0n) return;
      try {
        const amounts = await c.read.router.getAmountsOut(inWei, path);
        if (!mounted) return;
        const outWei = BigInt(amounts[1]);
        const outStr = parseFloat(formatUnits(outWei, toToken.decimals));
        setAmountOut(outStr.toString());
        const price = parseFloat(formatUnits(outWei, toToken.decimals)) / parseFloat(amountIn);
        setPriceStr(`1 ${fromToken.symbol} = ${price.toFixed(6)} ${toToken.symbol}`);
      } catch {
        if (!mounted) return;
        setPriceStr('Quote failed');
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountIn, dir, c.hasRouter]);

  if (!account) return <ConnectPrompt />;

  const setMax = () => {
    try { setAmountIn(formatUnits(fromToken.balance, fromToken.decimals)); } catch {}
  };

  const flip = () => {
    setDir((d) => d === 'grxToUsdt' ? 'usdtToGrx' : 'grxToUsdt');
    setAmountIn(amountOut || '');
    setAmountOut('');
  };

  const doSwap = async () => {
    try {
      if (!c.hasRouter) throw new Error('Router not configured');
      if (!amountIn) throw new Error('Enter an amount');
      const inWei = parseUnits(amountIn, fromToken.decimals);
      if (inWei <= 0n) throw new Error('Invalid amount');
      if (inWei > BigInt(fromToken.balance)) throw new Error('Insufficient balance');

      setBusy(true);
      const routerAddr = c.addresses.pancakeRouter;
      const fromContractWrite = dir === 'grxToUsdt' ? c.write.grx : c.write.usdt;
      const fromContractRead  = dir === 'grxToUsdt' ? c.read.grx  : c.read.usdt;

      const allow = await fromContractRead.allowance(account, routerAddr);
      if (BigInt(allow) < inWei) {
        setTxLabel(`Approving ${fromToken.symbol}`);
        const aTx = await fromContractWrite.approve(routerAddr, inWei);
        await aTx.wait();
        notify.success('Approved', `${fromToken.symbol} approved to router.`);
      }

      setTxLabel('Quoting');
      const amounts = await c.read.router.getAmountsOut(inWei, path);
      const outWei = BigInt(amounts[1]);
      const slipBps = BigInt(Math.max(0, Math.round(parseFloat(slippage || '0') * 100)));
      const minOut = outWei - (outWei * slipBps) / 10000n;

      setTxLabel(`Swapping ${fromToken.symbol} → ${toToken.symbol}`);
      const deadline = BigInt(Math.floor(Date.now() / 1000) + DEADLINE_MINUTES * 60);
      const tx = await c.write.router.swapExactTokensForTokens(inWei, minOut, path, account, deadline);
      await tx.wait();
      notify.success('Swapped', `${amountIn} ${fromToken.symbol} → ${parseFloat(formatUnits(outWei, toToken.decimals)).toFixed(6)} ${toToken.symbol}`);
      setAmountIn('');
      setAmountOut('');
    } catch (e) {
      notify.error('Swap Failed', parseError(e));
    } finally {
      setBusy(false); setTxLabel('');
    }
  };

  const priceDisplay = tickerPrice
    ? '$' + parseFloat(formatUnits(tickerPrice, 18)).toFixed(6)
    : '—';

  return (
    <div className="animate-fade-up dex-page">
      {/* ─────────── BIG TEXT HEADING ─────────── */}
      <div className="dex-title-wrap">
        <div className="dex-title grx-text-gradient">GRX DEX</div>
        <div className="dex-tagline">Instant GRX ↔ USDT · PancakeSwap v2</div>
      </div>

      {/* ─────────── TOKEN STRIP (logo + name + price) ─────────── */}
      <div className="token-strip">
        <img src="/grx.png" alt="GRX" className="token-strip-logo animate-breathe" />
        <div className="token-strip-info">
          <div className="ts-name">Graphenex</div>
          <div className="ts-pair">
            <span className="ts-symbol">GRX</span>
            <span className="ts-price">{priceDisplay}</span>
          </div>
        </div>
        <div className="token-strip-dot" />
      </div>

      {/* ─────────── COMPACT WALLET STRIP ─────────── */}
      <div className="wallet-strip-mini">
        <div className="wsm-item">
          <img src="/grx.png" alt="GRX" className="wsm-icon" />
          <span className="wsm-label">GRX</span>
          <span className="wsm-value">{fmtGRX(grxBal)}</span>
        </div>
        <div className="wsm-sep" />
        <div className="wsm-item">
          <img src="/usdt.png" alt="USDT" className="wsm-icon" />
          <span className="wsm-label">USDT</span>
          <span className="wsm-value">{fmtUSD(usdtBal)}</span>
        </div>
      </div>

      {/* ─────────── SWAP CARD ─────────── */}
      <div className="swap-shell">
        <GlassCard>
          <div className="swap-head">
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18 }}>
              Swap
            </div>
            <div className="slippage-control">
              <label>Slippage</label>
              <input
                type="number"
                step="0.1"
                value={slippage}
                onChange={(e) => setSlippage(e.target.value)}
              />
              <span>%</span>
            </div>
          </div>

          <div className="swap-box">
            <div className="swap-box-head">
              <span className="swap-side-label">From</span>
              <span className="swap-box-bal">
                {fromToken.symbol === 'GRX' ? fmtGRX(fromToken.balance) : fmtUSD(fromToken.balance)}
              </span>
            </div>
            <div className="swap-input-row">
              <input
                type="number"
                placeholder="0.0"
                value={amountIn}
                onChange={(e) => setAmountIn(e.target.value)}
              />
              <button className="max-btn-pill" onClick={setMax} type="button">MAX</button>
              <div className="token-chip">
                <img src={fromToken.logo} alt={fromToken.symbol} className="token-chip-img" />
                <span>{fromToken.symbol}</span>
              </div>
            </div>
          </div>

          <div className="swap-flip-wrap">
            <button className="swap-flip-btn" onClick={flip} aria-label="Flip">
              <Icon name="swap" size={18} />
            </button>
          </div>

          <div className="swap-box">
            <div className="swap-box-head">
              <span className="swap-side-label">To (estimated)</span>
              <span className="swap-box-bal">
                {toToken.symbol === 'GRX' ? fmtGRX(toToken.balance) : fmtUSD(toToken.balance)}
              </span>
            </div>
            <div className="swap-input-row">
              <input
                type="text"
                placeholder="0.0"
                value={amountOut}
                readOnly
                style={{ background: 'transparent', cursor: 'default' }}
              />
              <div className="token-chip">
                <img src={toToken.logo} alt={toToken.symbol} className="token-chip-img" />
                <span>{toToken.symbol}</span>
              </div>
            </div>
          </div>

          <div className="swap-details">
            <div className="sd-row">
              <span>Rate</span>
              <span>{priceStr || '—'}</span>
            </div>
            <div className="sd-row">
              <span>Slippage tolerance</span>
              <span>{slippage}%</span>
            </div>
            <div className="sd-row">
              <span>Route</span>
              <span className="mono">{fromToken.symbol} → {toToken.symbol}</span>
            </div>
          </div>

          <div className="mt-3">
            <NeonButton block size="lg" loading={busy} onClick={doSwap} disabled={!amountIn || !c.hasRouter}>
              {!c.hasRouter ? 'Router Not Configured' : (busy ? 'Processing' : 'Swap')}
            </NeonButton>
          </div>
        </GlassCard>
      </div>

      {busy && <HexLoader label={txLabel || 'Processing'} sub="Confirm the transaction in your wallet." />}
    </div>
  );
};

export default Swap;
